/**
 * Which app a page load is: the till, or a customer's table page.
 *
 * Decided once, from the path, before either is imported — see `main.tsx` for
 * why it cannot be a route in the till's router. Every path under `/t/` is the
 * customer's (nginx serves them all with the page's own headers); `/t` alone,
 * `/tables` and `/settings/tables` are the till's.
 */
export type Entry = { app: 'till' } | { app: 'table'; token: string | null }

const TABLE_PREFIX = '/t/'
const TABLE_PATH = /^\/t\/([^/]+)\/?$/u

/**
 * The backend's token exactly: `secrets.token_urlsafe(32)`, 43 characters of
 * the url-safe alphabet. Checked after decoding, so `%2F`, `.` or a space can
 * never reach the request path, the sessionStorage key or the page title.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/u

export function entryFromPath(pathname: string): Entry {
  if (!pathname.startsWith(TABLE_PREFIX)) return { app: 'till' }
  return { app: 'table', token: tokenFrom(pathname) }
}

/** `null` is any path under /t/ that is not a printed QR: the not-active screen, no request. */
function tokenFrom(pathname: string): string | null {
  const match = TABLE_PATH.exec(pathname)
  if (match === null) return null
  try {
    const token = decodeURIComponent(match[1] ?? '')
    return TOKEN_SHAPE.test(token) ? token : null
  } catch {
    // A malformed escape is not a token anyone printed.
    return null
  }
}

function cancelInstallPrompt(event: Event): void {
  event.preventDefault()
}

/**
 * The till's `index.html` links a manifest, which makes any page on this origin
 * installable as "CKP POS", and Chrome on Android offers that unprompted. On a
 * customer's phone it must never appear, so this runs synchronously in the
 * entry, before the table shell's chunk is even requested: a listener attached
 * after that import resolves could miss the event.
 *
 * Safe to call twice (the table shell calls it too): removing an absent link
 * is a no-op, and the same listener is only ever registered once.
 */
export function suppressInstallPrompt(): void {
  document.querySelector('link[rel="manifest"]')?.remove()
  window.addEventListener('beforeinstallprompt', cancelInstallPrompt)
}
